import {
  ArrowDown,
  ArrowRight,
  BarChart3,
  Bell,
  CheckCircle2,
  ChevronRight,
  Cloud,
  Database,
  Download,
  FlaskConical,
  Gauge,
  GitBranch,
  Globe2,
  LayoutGrid,
  Lock,
  Network,
  Plug,
  RefreshCcw,
  Rocket,
  Server,
  ShieldCheck,
  Target,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { RevealSection } from "@/components/Reveal";
import { SiteLayout } from "@/components/SiteLayout";
import { AppWindowFrame, RolloutControlsPanel } from "@/components/solutions/RolloutControlsPanel";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useSite } from "@/hooks/use-site";
import { localizeUrl } from "@/lib/site";

export function OtaUpdatesPage() {
  const { messages, site, href } = useSite();
  const home = localizeUrl("/", site.locale, href);
  const alcatraz = localizeUrl("/solutions/alcatraz-dms", site.locale, href);
  const demoCta = localizeUrl("/contacts?inquiry=demo#inquiry-form", site.locale, href);
  const salesCta = localizeUrl("/contacts?inquiry=sales#inquiry-form", site.locale, href);

  const heroStats = [
    { icon: Rocket, label: messages.otaHeroStat1 },
    { icon: Globe2, label: messages.otaHeroStat2 },
    { icon: Cloud, label: messages.otaHeroStat3 },
  ];

  const bandStats = [
    { value: messages.otaStatRolloutValue, label: messages.otaStatRolloutLabel },
    { value: messages.otaStatScaleValue, label: messages.otaStatScaleLabel },
    { value: messages.otaStatDeliveryValue, label: messages.otaStatDeliveryLabel },
    { value: messages.otaStatDeployValue, label: messages.otaStatDeployLabel },
  ];

  const workflowSteps: { icon: LucideIcon; title: string; body: string }[] = [
    {
      icon: FlaskConical,
      title: messages.otaReleaseCard4Title,
      body: messages.otaReleaseCard4Body,
    },
    {
      icon: ShieldCheck,
      title: messages.otaReleaseCard6Title,
      body: messages.otaReleaseCard6Body,
    },
    { icon: Rocket, title: messages.otaReleaseCard2Title, body: messages.otaReleaseCard2Body },
    { icon: BarChart3, title: messages.otaMonitorCard1Title, body: messages.otaMonitorCard1Body },
  ];

  const targetChips = [
    messages.otaTargetChip1,
    messages.otaTargetChip2,
    messages.otaTargetChip3,
    messages.otaTargetChip4,
    messages.otaTargetChip5,
    messages.otaTargetChip6,
  ];

  const governPerms = [
    messages.otaGovernPerm3,
    messages.otaGovernPerm4,
    messages.otaGovernPerm5,
    messages.otaGovernPerm6,
    messages.otaGovernPerm7,
  ];

  const deliveryCards: { icon: LucideIcon; title: string; body: string }[] = [
    { icon: Globe2, title: messages.otaDeliveryCard1Title, body: messages.otaDeliveryCard1Body },
    {
      icon: RefreshCcw,
      title: messages.otaDeliveryCard2Title,
      body: messages.otaDeliveryCard2Body,
    },
    { icon: Lock, title: messages.otaDeliveryCard3Title, body: messages.otaDeliveryCard3Body },
  ];

  const monitorPoints: { icon: LucideIcon; title: string; body: string }[] = [
    { icon: BarChart3, title: messages.otaMonitorCard1Title, body: messages.otaMonitorCard1Body },
    { icon: Gauge, title: messages.otaMonitorCard2Title, body: messages.otaMonitorCard2Body },
    { icon: Bell, title: messages.otaMonitorCard4Title, body: messages.otaMonitorCard4Body },
  ];

  const faqs = [
    { q: messages.otaFaqQ1, a: messages.otaFaqA1 },
    { q: messages.otaFaqQ2, a: messages.otaFaqA2 },
    { q: messages.otaFaqQ3, a: messages.otaFaqA3 },
    { q: messages.otaFaqQ4, a: messages.otaFaqA4 },
    { q: messages.otaFaqQ5, a: messages.otaFaqA5 },
    { q: messages.otaFaqQ6, a: messages.otaFaqA6 },
    { q: messages.otaFaqQ7, a: messages.otaFaqA7 },
  ];

  return (
    <SiteLayout>
      {/* Breadcrumb */}
      <div className="border-b border-border/60 bg-background/60">
        <nav
          aria-label={messages.breadcrumbLabel}
          className="mx-auto flex max-w-7xl items-center gap-1.5 px-6 py-3 text-xs text-muted-foreground"
        >
          <a href={home} className="hover:text-foreground">
            {messages.otaBreadcrumbHome}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <a href={localizeUrl("/solutions", site.locale, href)} className="hover:text-foreground">
            {messages.otaBreadcrumbSolutions}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <span className="text-foreground">{messages.otaBreadcrumbPage}</span>
        </nav>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div
          aria-hidden
          className="bg-grid pointer-events-none absolute inset-0 -z-10 opacity-50"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-32 top-0 -z-10 h-[480px] w-[640px] rounded-full opacity-40 blur-3xl"
          style={{ background: "var(--gradient-aurora)" }}
        />
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 py-16 lg:grid-cols-[1.05fr_1fr] lg:py-24">
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card px-3 py-1 text-xs text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
              {messages.otaHeroEyebrow}
            </div>
            <h1 className="mt-4 text-balance text-4xl font-semibold tracking-tight sm:text-5xl lg:text-5xl">
              {messages.otaHeroTitle}{" "}
              <span className="text-gradient">{messages.otaHeroTitleAccent}</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-muted-foreground">{messages.otaHeroSub}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="animate-glow w-full sm:w-auto">
                <a href={demoCta}>{messages.otaHeroCtaDemo}</a>
              </Button>
              <Button asChild size="lg" variant="outline" className="w-full sm:w-auto">
                <a href={salesCta}>{messages.otaHeroCtaSales}</a>
              </Button>
            </div>
            <p className="mt-6 inline-flex items-center gap-2 rounded-lg border border-border/60 bg-card/60 px-3 py-2 text-sm text-muted-foreground backdrop-blur">
              <Server className="h-4 w-4 shrink-0 text-primary" />
              <span>
                {messages.otaHeroPlatformNote}{" "}
                <a href={alcatraz} className="font-medium text-primary hover:underline">
                  Alcatraz DMS
                </a>
                .
              </span>
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {heroStats.map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card/60 px-3 py-1.5 text-xs text-foreground/90 backdrop-blur"
                >
                  <Icon className="h-3.5 w-3.5 text-primary" />
                  {label}
                </div>
              ))}
            </div>
          </div>

          <div className="animate-fade-up">
            <HeroRequestFlow messages={messages} />
          </div>
        </div>
      </section>

      {/* Stats band */}
      <RevealSection className="border-b border-border/60 section-tint py-12">
        <div className="mx-auto max-w-7xl px-6">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-8 md:grid-cols-4">
            {bandStats.map((s, i) => (
              <div
                key={s.value}
                className={`text-center md:text-left ${
                  i > 0 ? "md:border-l md:border-border/60 md:pl-6" : ""
                }`}
              >
                <dt className="text-2xl font-semibold tracking-tight sm:text-3xl">
                  <span className="text-gradient">{s.value}</span>
                </dt>
                <dd className="mx-auto mt-2 max-w-[15rem] text-sm leading-relaxed text-muted-foreground md:mx-0">
                  {s.label}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </RevealSection>

      {/* Release workflow + real product proof */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.otaReleaseEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.otaReleaseTitle}
            </h2>
            <p className="mt-4 text-base text-muted-foreground">{messages.otaReleaseDescription}</p>
          </div>

          <div className="mt-12 grid grid-cols-1 items-center gap-10 lg:grid-cols-[0.82fr_1.18fr] lg:gap-14">
            <ol className="space-y-3">
              {workflowSteps.map(({ icon: Icon, title, body }, index) => (
                <li
                  key={title}
                  className="flex gap-4 rounded-2xl border border-border/60 bg-card/60 p-4"
                >
                  <span className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" />
                    <span className="absolute -right-1.5 -top-1.5 grid h-5 w-5 place-items-center rounded-full border border-primary/20 bg-background text-[10px] font-semibold text-primary">
                      {index + 1}
                    </span>
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-foreground">{title}</span>
                    <span className="mt-1 block text-sm leading-relaxed text-muted-foreground">
                      {body}
                    </span>
                  </span>
                </li>
              ))}
            </ol>

            <ApprovalWorkflowPanel messages={messages} />
          </div>
        </div>
      </RevealSection>

      {/* Targeting, rollout and governance */}
      <RevealSection className="border-b border-border/60 section-alt py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.otaRolloutEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.otaRolloutTitle}
            </h2>
            <p className="mt-4 text-base text-muted-foreground">{messages.otaRolloutBody}</p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-2xl border border-primary/25 bg-card/80 p-6 shadow-glow">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Target className="h-5 w-5" />
              </span>
              <h3 className="mt-4 text-lg font-semibold text-foreground">
                {messages.otaReleaseCard1Title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {messages.otaReleaseCard1Body}
              </p>
              <p className="mt-5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {messages.otaTargetChipsLabel}
              </p>
              <ul className="mt-3 flex flex-wrap gap-2">
                {targetChips.map((chip) => (
                  <li
                    key={chip}
                    className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-background/80 px-3 py-1.5 text-xs font-medium text-foreground/90"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                    {chip}
                  </li>
                ))}
              </ul>
            </div>

            <RolloutMeter messages={messages} />
          </div>

          <div className="mt-6 grid grid-cols-1 gap-6 rounded-2xl border border-border/60 bg-card/60 p-6 lg:grid-cols-[0.72fr_1.28fr] lg:items-center sm:p-8">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-primary">
                {messages.otaGovernEyebrow}
              </p>
              <h3 className="mt-2 text-xl font-semibold tracking-tight text-foreground">
                {messages.otaGovernTitle}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {messages.otaGovernBody}
              </p>
            </div>
            <ul className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              {governPerms.map((perm) => (
                <li
                  key={perm}
                  className="flex items-start gap-2 rounded-xl border border-border/60 bg-background/70 px-3 py-2.5 text-sm text-foreground/90"
                >
                  <ShieldCheck className="ota-accent mt-0.5 h-4 w-4 shrink-0" />
                  {perm}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </RevealSection>

      {/* Delivery */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.otaDeliveryEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.otaDeliveryTitle}
            </h2>
            <p className="mt-4 text-base text-muted-foreground">
              {messages.otaDeliveryDescription}
            </p>
          </div>

          <ul className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-3">
            {deliveryCards.map(({ icon: Icon, title, body }) => (
              <li
                key={title}
                className="rounded-2xl border border-border/60 bg-card/60 p-5 backdrop-blur"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-sm font-semibold text-foreground">{title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{body}</p>
              </li>
            ))}
          </ul>

          {/* Architecture diagram */}
          <div className="mx-auto mt-12 max-w-4xl">
            <p className="text-center text-xs font-medium uppercase tracking-wider text-primary">
              {messages.otaDiagramEyebrow}
            </p>
            <h3 className="mt-2 text-center text-xl font-semibold tracking-tight text-foreground">
              {messages.otaDiagramTitle}
            </h3>
            <DeliveryDiagram messages={messages} />
          </div>
        </div>
      </RevealSection>

      {/* Deploy */}
      <RevealSection className="border-b border-border/60 section-alt py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.otaDeployEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.otaDeployTitle}
            </h2>
            <p className="mt-4 text-base text-muted-foreground">{messages.otaDeployDescription}</p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2">
            <DeploymentCard
              icon={Cloud}
              tag={messages.otaDeployCloudTag}
              title={messages.otaDeployCloudTitle}
              body={messages.otaDeployCloudBody}
              bullets={[
                messages.otaDeployCloudBullet1,
                messages.otaDeployCloudBullet2,
                messages.otaDeployCloudBullet3,
              ]}
              highlight
            />
            <DeploymentCard
              icon={Server}
              title={messages.otaDeployOnpremTitle}
              body={messages.otaDeployOnpremBody}
              bullets={[
                messages.otaDeployOnpremBullet1,
                messages.otaDeployOnpremBullet2,
                messages.otaDeployOnpremBullet3,
              ]}
            />
          </div>

          <div className="mt-6 flex flex-col gap-6 rounded-2xl border border-primary/20 bg-primary/[0.04] p-6 sm:p-8 md:flex-row md:items-center">
            <span className="ota-accent-chip flex h-12 w-12 shrink-0 items-center justify-center rounded-xl">
              <Gauge className="h-6 w-6" />
            </span>
            <div className="flex-1">
              <h3 className="text-lg font-semibold tracking-tight">
                {messages.otaDeployNodeTitle}
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">{messages.otaDeployNodeBody}</p>
              <ul className="mt-4 flex flex-wrap gap-2">
                {[
                  messages.otaDeployNodeChip1,
                  messages.otaDeployNodeChip2,
                  messages.otaDeployNodeChip3,
                ].map((chip) => (
                  <li
                    key={chip}
                    className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-background/60 px-3 py-1 text-xs font-medium text-foreground/80"
                  >
                    <CheckCircle2 className="ota-accent h-3.5 w-3.5" />
                    {chip}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </RevealSection>

      {/* Monitoring — dashboard + points */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 lg:grid-cols-2">
          <div className="order-1 lg:order-2">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.otaMonitorEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.otaMonitorTitle}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              {messages.otaMonitorDescription}
            </p>
            <ul className="mt-6 space-y-4">
              {monitorPoints.map(({ icon: Icon, title, body }) => (
                <li key={title} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="text-sm text-foreground/90">
                    <span className="font-semibold text-foreground">{title}.</span> {body}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm text-muted-foreground">{messages.otaMonitorMetricsNote}</p>
          </div>
          <div className="order-2 lg:order-1">
            <RolloutControlsPanel messages={messages} />
          </div>
        </div>
      </RevealSection>

      {/* Advanced capabilities */}
      <RevealSection className="border-b border-border/60 section-alt py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.otaAdvancedEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.otaAdvancedTitle}
            </h2>
            <p className="mt-4 text-base text-muted-foreground">{messages.otaAdvancedBody}</p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <article className="rounded-2xl border border-border/60 bg-card/70 p-6 sm:p-8">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <GitBranch className="h-5 w-5" />
              </span>
              <p className="mt-5 text-xs font-medium uppercase tracking-wider text-primary">
                {messages.otaMigrateEyebrow}
              </p>
              <h3 className="mt-2 text-2xl font-semibold tracking-tight">
                {messages.otaMigrateTitle}
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                {messages.otaMigrateBody}
              </p>
              <ul className="mt-5 space-y-2 text-sm text-foreground/90">
                {[
                  messages.otaMigrateBullet1,
                  messages.otaMigrateBullet2,
                  messages.otaMigrateBullet3,
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <ChevronRight className="mt-1 h-3.5 w-3.5 shrink-0 text-primary" />
                    {b}
                  </li>
                ))}
              </ul>
            </article>

            <article className="rounded-2xl border border-border/60 bg-card/70 p-6 sm:p-8">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <LayoutGrid className="h-5 w-5" />
              </span>
              <p className="mt-5 text-xs font-medium uppercase tracking-wider text-primary">
                {messages.otaAppsEyebrow}
              </p>
              <h3 className="mt-2 text-2xl font-semibold tracking-tight">
                {messages.otaAppsTitle}
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                {messages.otaAppsBody}
              </p>
              <ul className="mt-5 space-y-2 text-sm text-foreground/90">
                {[messages.otaAppsBullet1, messages.otaAppsBullet2].map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <ChevronRight className="mt-1 h-3.5 w-3.5 shrink-0 text-primary" />
                    {b}
                  </li>
                ))}
              </ul>
            </article>

            <article className="rounded-2xl border border-border/60 bg-card/70 p-6 sm:p-8 lg:col-span-2">
              <div className="grid gap-8 lg:grid-cols-2 lg:items-center lg:gap-12">
                <div>
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Plug className="h-5 w-5" />
                  </span>
                  <p className="mt-5 text-xs font-medium uppercase tracking-wider text-primary">
                    {messages.otaApiEyebrow}
                  </p>
                  <h3 className="mt-2 text-2xl font-semibold tracking-tight">
                    {messages.otaApiTitle}
                  </h3>
                  <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                    {messages.otaApiBody}
                  </p>
                </div>
                <div>
                  <ApiPanel messages={messages} />
                </div>
              </div>
            </article>
          </div>
        </div>
      </RevealSection>

      {/* FAQ */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <div className="text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.otaFaqEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.otaFaqTitle}
            </h2>
          </div>
          <Accordion type="single" collapsible className="mt-10">
            {faqs.map(({ q, a }, i) => (
              <AccordionItem key={i} value={`q${i}`}>
                <AccordionTrigger className="text-base font-medium">{q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                  {a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </RevealSection>

      {/* Final CTA */}
      <RevealSection className="relative overflow-hidden bg-foreground py-16 text-background sm:py-20">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-0 opacity-30"
          style={{ background: "var(--gradient-aurora)" }}
        />
        <div className="relative z-10 mx-auto max-w-3xl px-6 text-center">
          <p className="text-xs font-medium uppercase tracking-wider text-background/70">
            {messages.otaFinalEyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
            {messages.otaFinalTitle}
          </h2>
          <p className="mt-4 text-base text-background/80">{messages.otaFinalBody}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" variant="secondary" className="w-full sm:w-auto">
              <a href={demoCta}>{messages.otaFinalCta}</a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="w-full border-background/40 bg-transparent text-background hover:bg-background/10 hover:text-background sm:w-auto"
            >
              <a href={salesCta}>{messages.otaFinalCtaSecondary}</a>
            </Button>
          </div>
        </div>
      </RevealSection>
    </SiteLayout>
  );
}

type Msgs = ReturnType<typeof useSite>["messages"];

function HeroRequestFlow({ messages }: { messages: Msgs }) {
  // -1 = idle (revealed but not yet started); 0-2 evaluate; 3 download; 4 install; 5 complete.
  const [phase, setPhase] = useState(-1);
  const figureRef = useRef<HTMLElement>(null);
  const outcomeState =
    phase < 3 ? "ready" : phase === 3 ? "downloading" : phase === 4 ? "installing" : "complete";
  const outcomeLabel =
    outcomeState === "downloading"
      ? messages.otaHeroDiagramDownloading
      : outcomeState === "installing"
        ? messages.otaHeroDiagramInstalling
        : outcomeState === "complete"
          ? messages.otaHeroDiagramInstalled
          : messages.otaHeroDiagramOutcome;
  const steps = [
    { icon: Target, label: messages.otaHeroDiagramStep1 },
    { icon: Rocket, label: messages.otaHeroDiagramStep2 },
    { icon: Globe2, label: messages.otaHeroDiagramStep3 },
  ];

  useEffect(() => {
    const el = figureRef.current;
    if (!el) return;

    const motionPreference = window.matchMedia("(prefers-reduced-motion: reduce)");
    let timer: number | undefined;

    function stop() {
      window.clearTimeout(timer);
    }

    const phaseDurations = [1500, 1500, 1500, 2400, 2100, 5500];

    // Evaluate in three steps, download, install, then dwell on the completed state.
    function runStep(step: number) {
      setPhase(step);
      timer = window.setTimeout(
        () => runStep(step < phaseDurations.length - 1 ? step + 1 : 0),
        phaseDurations[step],
      );
    }

    function start() {
      stop();
      if (motionPreference.matches) {
        setPhase(5);
        return;
      }
      // Keep the evaluation rows idle for 1.5s after reveal while the header
      // refresh icon is already spinning, then walk the normal sequence.
      setPhase(-1);
      timer = window.setTimeout(() => runStep(0), 1500);
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          start();
        } else {
          stop();
          setPhase(-1);
        }
      },
      { threshold: 0.33 },
    );

    observer.observe(el);
    motionPreference.addEventListener("change", start);

    return () => {
      stop();
      observer.disconnect();
      motionPreference.removeEventListener("change", start);
    };
  }, []);

  return (
    <figure
      ref={figureRef}
      className="ota-request-flow relative mx-auto max-w-[480px]"
      data-complete={phase >= 5}
      data-transfer={phase === 3 || phase === 4}
    >
      <div
        aria-hidden
        className="ota-request-glow absolute -inset-6 -z-10 rounded-full bg-primary/15 blur-3xl"
      />
      <div className="rounded-2xl border border-border/60 bg-card/80 p-6 shadow-xl shadow-primary/10 backdrop-blur">
        <div className="flex items-center gap-3 border-b border-border/60 pb-4">
          <span
            className="ota-request-header-icon flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary"
            data-active={phase < 5}
          >
            <RefreshCcw className="h-5 w-5" />
          </span>
          <div>
            <p className="text-sm font-semibold text-foreground">{messages.otaHeroDiagramTitle}</p>
            <p className="ota-request-subtitle text-xs text-muted-foreground">
              {phase >= 5
                ? messages.otaHeroDiagramSubDone
                : phase >= 3
                  ? outcomeLabel
                  : messages.otaHeroDiagramSubRunning}
            </p>
          </div>
        </div>
        <ol className="mt-4 space-y-2.5">
          {steps.map(({ icon: Icon, label }, i) => {
            const state = phase >= 3 || i < phase ? "complete" : i === phase ? "active" : "pending";

            return (
              <li
                key={label}
                className="ota-request-step flex items-center gap-3 rounded-xl border border-border/60 bg-background/60 px-3 py-2.5"
                data-state={state}
              >
                <span className="ota-request-step-icon flex h-8 w-8 items-center justify-center rounded-lg bg-primary/5 text-primary">
                  <Icon className="h-4 w-4" />
                </span>
                <span className="relative z-10 text-sm text-foreground/90">{label}</span>
                <span
                  aria-hidden
                  className="ota-request-step-status relative z-10 ml-auto grid h-5 w-5 shrink-0 place-items-center"
                >
                  <span className="ota-request-step-number text-xs font-medium text-muted-foreground/50">
                    {i + 1}
                  </span>
                  <RefreshCcw className="ota-request-step-working absolute h-4 w-4 text-primary" />
                  <CheckCircle2 className="ota-request-step-complete absolute h-4.5 w-4.5 text-primary" />
                </span>
                <span aria-hidden className="ota-request-step-progress">
                  <span />
                </span>
                <span aria-hidden className="ota-request-step-sheen" />
              </li>
            );
          })}
        </ol>
        <div
          aria-hidden
          className="ota-request-connector flex justify-center py-2 text-primary/50"
          data-state={outcomeState}
        >
          <ArrowDown className="h-4 w-4" />
        </div>
        <div
          className="ota-request-outcome flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/[0.06] px-4 py-3"
          data-state={outcomeState}
        >
          <span aria-hidden className="ota-request-transfer-progress" />
          <span className="ota-request-outcome-icon grid h-5 w-5 shrink-0 place-items-center rounded-full border border-primary/60 text-primary">
            {outcomeState === "complete" ? (
              <CheckCircle2 className="h-5 w-5" />
            ) : outcomeState === "downloading" ? (
              <Download className="h-3.5 w-3.5" />
            ) : outcomeState === "installing" ? (
              <RefreshCcw className="h-3.5 w-3.5" />
            ) : (
              <CheckCircle2 className="h-5 w-5" />
            )}
          </span>
          <span className="relative z-10 text-sm font-medium text-foreground">{outcomeLabel}</span>
        </div>
      </div>
    </figure>
  );
}

function RolloutMeter({ messages }: { messages: Msgs }) {
  const stages = [
    { label: messages.otaRolloutMeterStage1, pct: 2 },
    { label: messages.otaRolloutMeterStage2, pct: 10 },
    { label: messages.otaRolloutMeterStage3, pct: 45 },
    { label: messages.otaRolloutMeterStage4, pct: 100 },
  ];
  return (
    <figure className="relative">
      <div aria-hidden className="absolute -inset-4 -z-10 rounded-3xl bg-primary/10 blur-2xl" />
      <div className="rounded-2xl border border-border/60 bg-card/80 p-6 shadow-xl shadow-primary/10 backdrop-blur">
        <div className="flex items-center justify-between border-b border-border/60 pb-3">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {messages.otaRolloutMeterLabel}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-foreground/90">
            <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
            {messages.otaLiveLabel}
          </span>
        </div>
        <ul className="mt-4 space-y-4">
          {stages.map(({ label, pct }, i) => (
            <li key={label}>
              <div className="flex items-center justify-between text-xs">
                <span className="font-medium text-foreground/90">{label}</span>
                <span className="tabular-nums text-muted-foreground">{pct}%</span>
              </div>
              <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-muted">
                <div
                  className={`h-full rounded-full ${
                    i === stages.length - 1 ? "ota-accent-bar" : "bg-primary/55"
                  }`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      </div>
    </figure>
  );
}

function ApprovalWorkflowPanel({ messages }: { messages: Msgs }) {
  const [completedSteps, setCompletedSteps] = useState(0);
  const [workingStep, setWorkingStep] = useState<number | null>(null);
  const actions: {
    icon: LucideIcon;
    title: string;
    body: string;
    action: string;
  }[] = [
    {
      icon: FlaskConical,
      title: messages.otaUiTestTitle,
      body: messages.otaUiTestBody,
      action: messages.otaUiTestAction,
    },
    {
      icon: ShieldCheck,
      title: messages.otaUiConfirmTitle,
      body: messages.otaUiConfirmBody,
      action: messages.otaUiConfirmAction,
    },
    {
      icon: Rocket,
      title: messages.otaUiReleaseTitle,
      body: messages.otaUiReleaseBody,
      action: messages.otaUiReleaseAction,
    },
  ];

  function advance(step: number) {
    if (step !== completedSteps || workingStep !== null) return;
    setWorkingStep(step);
    window.setTimeout(() => {
      setCompletedSteps(step + 1);
      setWorkingStep(null);
    }, 650);
  }

  return (
    <figure className="relative" aria-label={messages.otaReleaseTitle}>
      <div aria-hidden className="absolute -inset-4 -z-10 rounded-3xl bg-primary/10 blur-2xl" />
      <AppWindowFrame messages={messages} path="alcatraz.inext.ltd / firmware / approval">
        <dl className="divide-y divide-border/60 px-4 sm:px-6">
          {actions.map(({ icon: Icon, title, body, action }, index) => {
            const isComplete = index < completedSteps;
            const isActive = index === completedSteps;
            const isWorking = index === workingStep;

            return (
              <div
                key={title}
                className={`grid gap-4 py-5 transition-opacity sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center ${!isComplete && !isActive ? "opacity-55" : ""}`}
              >
                <dt className="flex items-start gap-4">
                  <span
                    className={`mt-0.5 hidden h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors sm:flex ${
                      isComplete
                        ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                        : isActive
                          ? "bg-primary/10 text-primary"
                          : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {isComplete ? (
                      <CheckCircle2 className="h-5 w-5" />
                    ) : (
                      <Icon className="h-5 w-5" />
                    )}
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-foreground">{title}</span>
                    <span className="mt-1 block text-sm leading-relaxed text-muted-foreground">
                      {body}
                    </span>
                  </span>
                </dt>
                <dd className="sm:pl-4">
                  <span className="relative inline-flex">
                    {index === 0 && isActive && !isWorking && (
                      <span
                        aria-hidden
                        className="ota-action-attention pointer-events-none absolute -inset-1 rounded-lg border-2 border-primary/50"
                      />
                    )}
                    <button
                      type="button"
                      disabled={!isActive || isWorking}
                      onClick={() => advance(index)}
                      className={`relative inline-flex h-9 min-w-36 items-center justify-center gap-2 rounded-md px-3 text-sm font-medium shadow-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                        isComplete
                          ? "bg-emerald-500/15 text-emerald-700 shadow-none dark:text-emerald-300"
                          : isActive
                            ? "bg-primary text-primary-foreground hover:-translate-y-0.5 hover:shadow-md"
                            : "cursor-not-allowed bg-primary/25 text-primary-foreground/70 shadow-none"
                      }`}
                    >
                      {isWorking && <RefreshCcw className="h-4 w-4 animate-spin" />}
                      {isComplete
                        ? messages.otaUiCompletedAction
                        : isWorking
                          ? messages.otaUiWorkingAction
                          : action}
                    </button>
                  </span>
                </dd>
              </div>
            );
          })}
        </dl>
        <div
          className="ota-approval-success"
          data-open={completedSteps === actions.length}
          aria-hidden={completedSteps !== actions.length}
        >
          <div className="min-h-0 overflow-hidden">
            <div
              className="mx-4 mb-4 flex flex-col gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 sm:mx-6 sm:flex-row sm:items-center sm:justify-between"
              aria-live="polite"
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                <CheckCircle2 className="h-5 w-5 shrink-0" />
                {messages.otaUiWorkflowComplete}
              </span>
              <button
                type="button"
                disabled={completedSteps !== actions.length}
                tabIndex={completedSteps === actions.length ? 0 : -1}
                onClick={() => setCompletedSteps(0)}
                className="inline-flex h-8 items-center justify-center gap-2 rounded-md px-3 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 dark:text-emerald-300"
              >
                <RefreshCcw className="h-3.5 w-3.5" />
                {messages.otaUiReplayAction}
              </button>
            </div>
          </div>
        </div>
      </AppWindowFrame>
    </figure>
  );
}

function ApiPanel({ messages }: { messages: Msgs }) {
  const endpoints = [
    { method: "GET", path: "/firmware/builds" },
    { method: "POST", path: "/releases" },
    { method: "POST", path: "/releases/{id}/approve" },
    { method: "GET", path: "/devices/{mac}" },
    { method: "GET", path: "/rollouts/{id}/adoption" },
  ];
  return (
    <figure className="relative">
      <div aria-hidden className="absolute -inset-4 -z-10 rounded-3xl bg-primary/10 blur-2xl" />
      <div className="overflow-hidden rounded-2xl border border-border/60 bg-card/80 shadow-xl shadow-primary/10 backdrop-blur">
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
          <span className="text-sm font-semibold text-foreground">{messages.otaApiPanelTitle}</span>
          <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            {messages.otaApiPanelSub}
          </span>
        </div>
        <ul className="divide-y divide-border/60 font-mono text-sm">
          {endpoints.map((e) => (
            <li key={e.path} className="flex items-center gap-3 px-5 py-3">
              <span
                className={`inline-flex w-12 shrink-0 justify-center rounded px-1.5 py-0.5 text-[10px] font-semibold tracking-wide ${
                  e.method === "POST"
                    ? "bg-primary/15 text-primary"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                {e.method}
              </span>
              <span className="truncate text-foreground/90">{e.path}</span>
            </li>
          ))}
        </ul>
      </div>
    </figure>
  );
}

function DeploymentCard({
  icon: Icon,
  title,
  body,
  bullets,
  tag,
  highlight,
}: {
  icon: LucideIcon;
  title: string;
  body: string;
  bullets: string[];
  tag?: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`rounded-2xl border p-6 backdrop-blur ${
        highlight ? "border-primary/30 bg-card/80 shadow-glow" : "border-border/60 bg-card/60"
      }`}
    >
      <div className="flex items-start justify-between">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Icon className="h-5 w-5" />
        </span>
        {tag && (
          <span className="rounded-full bg-primary/10 px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider text-foreground/90">
            {tag}
          </span>
        )}
      </div>
      <h3 className="mt-5 text-xl font-semibold tracking-tight text-foreground">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
      <ul className="mt-5 space-y-2 text-sm text-foreground/90">
        {bullets.map((b) => (
          <li key={b} className="flex items-start gap-2">
            <ChevronRight className="mt-1 h-3.5 w-3.5 shrink-0 text-primary" />
            {b}
          </li>
        ))}
      </ul>
    </div>
  );
}

function DiagramNode({
  icon: Icon,
  label,
  highlight,
}: {
  icon: LucideIcon;
  label: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`flex items-center gap-3 rounded-xl border px-4 py-3 backdrop-blur ${
        highlight ? "border-primary/40 bg-primary/[0.06]" : "border-border/60 bg-card/70"
      }`}
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Icon className="h-4.5 w-4.5" />
      </span>
      <span className="text-sm font-medium text-foreground/90">{label}</span>
    </div>
  );
}

function DiagramConnector() {
  return (
    <div aria-hidden className="flex justify-center py-2.5 text-primary/60">
      <ArrowDown className="h-5 w-5" />
    </div>
  );
}

function DeliveryDiagram({ messages }: { messages: Msgs }) {
  return (
    <div className="mt-8 rounded-2xl border border-border/60 bg-background/70 p-6 shadow-sm sm:p-8">
      {/* Compact mobile path */}
      <div className="sm:hidden">
        <DiagramNode icon={Target} label={messages.otaDiagramNode3} />
        <DiagramConnector />
        <DiagramNode icon={Database} label={messages.otaDiagramNode5} />
        <DiagramConnector />
        <DiagramNode icon={Network} label={messages.otaDiagramNode7} highlight />
      </div>

      <div className="hidden sm:block">
        {/* Control chain */}
        <div className="grid grid-cols-2 gap-3">
          <DiagramNode icon={Server} label={messages.otaDiagramNode1} />
          <DiagramNode icon={ShieldCheck} label={messages.otaDiagramNode2} />
          <DiagramNode icon={Target} label={messages.otaDiagramNode3} />
          <DiagramNode icon={Rocket} label={messages.otaDiagramNode4} highlight />
        </div>

        <DiagramConnector />

        {/* Storage tier */}
        <div className="grid grid-cols-2 gap-3">
          <DiagramNode icon={Database} label={messages.otaDiagramNode5} />
          <DiagramNode icon={Globe2} label={messages.otaDiagramNode6} />
        </div>

        <DiagramConnector />

        {/* Fleet */}
        <div className="mx-auto max-w-md">
          <div className="flex items-center gap-3 rounded-xl border border-primary/40 bg-primary/[0.08] px-4 py-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
              <Network className="h-4.5 w-4.5" />
            </span>
            <span className="text-sm font-semibold text-foreground">
              {messages.otaDiagramNode7}
            </span>
          </div>
        </div>
      </div>

      {/* Telemetry return */}
      <div className="mt-5 flex flex-wrap items-center justify-center gap-2 rounded-xl border border-dashed border-border/60 bg-background/40 px-4 py-2.5 text-xs text-muted-foreground">
        <BarChart3 className="h-3.5 w-3.5 text-primary" />
        <span>{messages.otaDiagramNode8}</span>
        <ArrowRight className="h-3.5 w-3.5" />
        <span>{messages.otaDiagramReturnLabel}</span>
      </div>
    </div>
  );
}
